import { useEffect, useRef, useState } from "react";
import axios from "../../api/axios";
import { toast } from "react-toastify";
import CirclePerc from "../../components/CirclePerc/CirclePerc";
import StreamTranscriptItem from "../../components/Streams/StreamTranscriptItem";
import HMSTimeField from "../../components/forms/HMSTimeField/HMSTimeField";
import RadioButtonGroup from "../../components/forms/RadioButtonGroup/RadioButtonGroup";
import FormDropDown from "../../components/forms/DropDownList/FormDropDown";
import DropDownList from "../../components/forms/DropDownList/DropDownList";
import { convertMSToHMS } from "../../helpers/Dates";
import { IconJsxer } from "../../helpers/IconHelper";
import { CalculateEngagementTime, UserInteractionMessageChartData } from "../../helpers/AnalyticsFunctions";
import LeftColorBlockHeadedPanel from "../../components/LeftColorBlockBox/LeftColorBlockHeadedPanel";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { getLiveStreamAnalyticData } from "../../api/routes";

const MS_TIMES = {
    SECS_10: 10000,
    SECS_30: 30000,
    MINS_1: 60000,
    MINS_5: 300000,
    MINS_15: 900000,
};

const TabStreamManager = (props) => {
    const streamTargetTime = props._streamData.endTime - props._streamData.startTime;
    const targetTimeHms = convertMSToHMS(streamTargetTime, true);
    const liveTimeObj = props._streamLiveTime || {hoursStr: '00', minutesStr: '00', secondsStr: '00', rawHours: 0, rawMinutes: 0, rawSeconds: 0};
    const liveTimeSecs = liveTimeObj.rawHours * 3600 + liveTimeObj.rawMinutes * 60 + liveTimeObj.rawSeconds;
    const liveTimePerc = liveTimeSecs / (streamTargetTime / 1000) * 100;
    const hasChannels = Boolean(props._hasChannels);
    const showConnectPrompt = !props._chatConnected && hasChannels;
    const [channelFilter, setChannelFilter] = useState(null);
    const [messagePerTime, setMessagePerTime] = useState(MS_TIMES.SECS_10);
    const [selectedMessagePerTime, setSelectedMessagePerTime] = useState(null);
    const [pollingLiveAnlyticsData, setPollingLiveAnlyticsData] = useState(0);
    const lastChartSyncRef = useRef({ messages: 0, interactions: 0 });
    const chartSyncTimeoutRef = useRef(null);
    const lastChartSyncAtRef = useRef(0);
    const readinessHeading = !hasChannels
        ? 'No channels added to this stream'
        : !props._chatConnected
            ? 'You are not ready to stream yet!'
            : 'Channels connected - open the extension page';
    const readinessCopy = !hasChannels
        ? 'Add at least one channel to this stream before you can connect it.'
        : !props._chatConnected
            ? 'To enable all features, you need to make sure you open the OBS extension page and click the Connect to Channels button.'
            : 'Channels are connected. To enable all features, make sure the OBS extension page is open.';
    const totalUniqueUsers = Array.isArray(props._analyticsData.userList) ? props._analyticsData.userList.length : 0;
    const showConnectedPanel = props._chatConnected && props._featureReady;
    const safeAnalyticsData = {
        ...props._analyticsData,
        userList: Array.isArray(props._analyticsData.userList) ? props._analyticsData.userList : [],
        channels: Array.isArray(props._analyticsData.channels) ? props._analyticsData.channels : [],
    };
    const hasChartAnalytics = Boolean(safeAnalyticsData.featureStartTime);
    const channelDropdownItems = [{ id: 'all', label: 'All' }];
    if (safeAnalyticsData.channels.length > 1) {
        for (let i = 0; i < safeAnalyticsData.channels.length; i++) {
            const channelObj = safeAnalyticsData.channels[i];
            if (channelObj.channel) {
                channelDropdownItems.push({ id: channelObj.channel, label: channelObj.channel.toUpperCase() });
            }
        }
    }
    const chartData = hasChartAnalytics
        ? UserInteractionMessageChartData(safeAnalyticsData.featureStartTime, Date.now(), messagePerTime, safeAnalyticsData, null, channelFilter)
        : [];
    const isConfigOverlayActive = props._overlayState === 'configoverlay';

    const copyStreamLinkToClipboard = (e) =>{
        navigator.clipboard.writeText(props._streamLink);
        toast.success("Feature URL copied to clipboard!");
    }

    const updateChannelFilterHandler = (e, id, item) => {
        setChannelFilter(item);
    }

    const toggleConfigOverlay = () => {
        props._setOverlayState(isConfigOverlayActive ? 'idle' : 'configoverlay');
    }

    const getFullAnalyticData = () => {
        if (!props._chatConnected || !props._streamStarted) {
            return;
        }

        axios
            .get(getLiveStreamAnalyticData, {
                params: { streamID: props._streamID },
                withCredentials: true
            })
            .then(function (response) {
                props._updateAnalyticsFunc(response.data.analyticsData);
            })
            .catch(function (error) {
                toast.error('Error - ' + error);
            });

        setPollingLiveAnlyticsData(0);
    }

    useEffect(() => {
        if (!props._chatConnected || !props._streamStarted) {
            return;
        }

        getFullAnalyticData();
    }, [props._chatConnected, props._streamStarted, props._streamID]);

    useEffect(() => {
        if (!props._chatConnected || !props._streamStarted) {
            return;
        }

        const messages = Number(props._analyticsData?.messages || 0);
        const interactions = Number(props._analyticsData?.interactions || 0);
        const totalsAdvanced = messages > lastChartSyncRef.current.messages || interactions > lastChartSyncRef.current.interactions;

        if (!totalsAdvanced) {
            return;
        }

        const syncChartData = () => {
            lastChartSyncAtRef.current = Date.now();
            lastChartSyncRef.current = { messages, interactions };
            chartSyncTimeoutRef.current = null;
            getFullAnalyticData();
        };

        const timeSinceLastChartSync = Date.now() - lastChartSyncAtRef.current;
        if (timeSinceLastChartSync >= 5000) {
            syncChartData();
            return;
        }

        if (!chartSyncTimeoutRef.current) {
            chartSyncTimeoutRef.current = setTimeout(syncChartData, 5000 - timeSinceLastChartSync);
        }
    }, [props._analyticsData?.messages, props._analyticsData?.interactions, props._chatConnected, props._streamStarted, props._streamID]);

    useEffect(() => {
        return () => {
            if (chartSyncTimeoutRef.current) {
                clearTimeout(chartSyncTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!props._streamLiveTime) {
            return;
        }

        setPollingLiveAnlyticsData((prevState) => prevState + 1);
    }, [props._streamLiveTime]);

    useEffect(() => {
        if (pollingLiveAnlyticsData === 60) {
            getFullAnalyticData();
        }
    }, [pollingLiveAnlyticsData]);

    const [hasClawMachine, setHasClawMachine] = useState(false);
    useEffect(() => {
        // iterate through props._featureList to check if Claw Machine feature is available
        // If the varKey === 'clawMachine', set hasClawMachine to true
        props._featureList.forEach(feature => {
            if (feature.varKey === 'clawMachine') {
                setHasClawMachine(true);
            }
        });
    }, [props._featureList]);

    return (
        <div className="stream-manager-tab-layout">
            {showConnectedPanel &&
                <LeftColorBlockHeadedPanel
                    style={'campaign_live'}
                    className="stream-status-panel stream-connected-panel"
                    headingContent={
                        <div className="fl-row grow stream-status-panel-heading-row">
                            <h4>You are all connected</h4>
                            <div className="grow"></div>
                            <div className="shrink">
                                <button className={`config-overlay-toggle-btn${isConfigOverlayActive ? ' is-active' : ''}`} onClick={toggleConfigOverlay}>Show Config Overlay</button>
                            </div>
                            <div className="shrink">
                                <button className="standard-button force-stop-btn" onClick={props._forceStop}>FORCE RELOAD</button>
                            </div>
                        </div>
                    }
                >
                    <div className="stream-connected-panel-content">
                        <div className="stream-connected-panel-main">
                            <div className="stream-connected-panel-tools">
                                <div className="stream-status-panel-copy">
                                    <div className="stream-status-panel-links fl-row">
                                        <h5 className="label">OBS Extension Link:</h5>
                                        <div className="primary-color">
                                            <a href={props._streamLink} target='_blank'>Open Campaign Studio Extension {IconJsxer.GetIcon(IconJsxer.ICONS.link, IconJsxer.ICON_STYLES.standard)}</a>
                                        </div>
                                        <div className="primary-color button-active" onClick={copyStreamLinkToClipboard}>
                                            {IconJsxer.GetIcon(IconJsxer.ICONS.copy, IconJsxer.ICON_STYLES.standard)}
                                        </div>
                                        <h5 className="label">Preview Mode Link:</h5>
                                        <div className="primary-color">
                                            <a href={props._streamLink + '?preview'} target='_blank'>Open Campaign Studio Extension Preview</a>
                                        </div>
                                    </div>
                                    <div>You are all set. If something stops working during the stream, press <span className="force-stop-text">FORCE RELOAD</span> and follow the instructions to reset the extension.</div>
                                </div>
                            </div>
                            <div className="stream-connected-panel-copy">
                                {!props._audioEnabled &&
                                    <div className="stream-connection-warning stream-connected-panel-warning">
                                        <div className="stream-connection-warning-icon">{IconJsxer.GetIcon(IconJsxer.ICONS.error, IconJsxer.ICON_STYLES.standard)}</div>
                                        <div>User interaction may be required to enable audio and video. If audio or video does not play, show the config overlay and click inside the extension page. When the config overlay is displayed, you will see status in the feature and the audio icon will turn green.</div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </LeftColorBlockHeadedPanel>
            }

            {!showConnectedPanel &&
                <LeftColorBlockHeadedPanel 
                    style={'warning_orange'}
                    className="stream-status-panel"
                    headingContent={
                        <div className="fl-row grow stream-status-panel-heading-row">
                            <h4>{readinessHeading}</h4>
                            <div className="grow"></div>
                            <div className="shrink">
                                <button className={`config-overlay-toggle-btn${isConfigOverlayActive ? ' is-active' : ''}`} onClick={toggleConfigOverlay}>Show Config Overlay</button>
                            </div>
                            {showConnectPrompt &&
                                <div className="shrink">
                                    <button className={`standard-button tight${props._canActivateChat ? '' : ' button-inactive'}`} onClick={props._canActivateChat ? props._activateChat : null}>Connect to Channels</button>
                                </div>
                            }
                        </div>
                    }
                >
                    <div className="stream-status-panel-copy">
                        <div className="stream-status-panel-links fl-row">
                            <h5 className="label">OBS Extension Link:</h5>
                            <div className="primary-color">
                                <a href={props._streamLink} target='_blank'>Open Campaign Studio Extension {IconJsxer.GetIcon(IconJsxer.ICONS.link, IconJsxer.ICON_STYLES.standard)}</a>
                            </div>
                            <div className="primary-color button-active" onClick={copyStreamLinkToClipboard}>
                                {IconJsxer.GetIcon(IconJsxer.ICONS.copy, IconJsxer.ICON_STYLES.standard)}
                            </div>
                            <h5 className="label">Preview Mode Link:</h5>
                            <div className="primary-color">
                                <a href={props._streamLink + '?preview'} target='_blank'>Open Campaign Studio Extension Preview</a>
                            </div>
                            {hasClawMachine &&
                                <>  
                                    <h5 className="label">Claw Controller Link:</h5>
                                    <div className="primary-color">
                                        <a href={(props._streamLink).split('obs-feature').join('claw-controller')} target='_blank'>Open Campaign Studio Claw Controller</a>
                                    </div>
                                </>
                            }
                        </div>
                        <div>{readinessCopy}</div>
                    </div>
                </LeftColorBlockHeadedPanel>
            }

            <div className="split-content">
                <div className="column">
                    <div className="stream-manager-section-box">
                        <div className="underlined-panel-heading">
                            <h4>Stream Overview</h4>
                            <div className="grow"></div>
                        </div>

                        <div className="stream-manager-section-body stream-manager-overview-scroll">
                            {!props._chatConnected &&
                                <div className="stream-not-started">
                                    <div className="fl-column">
                                        <div className="pause-button">{IconJsxer.GetIcon(IconJsxer.ICONS.pause, IconJsxer.ICON_STYLES.roundPanelButton)}</div>
                                        <div>{hasChannels ? 'CHANNEL NOT CONNECTED' : 'NO CHANNELS ADDED'}</div>
                                    </div>
                                </div>
                            }
                            {props._chatConnected && !props._streamStarted &&
                                <div className="stream-not-started">
                                    <div className="fl-column">
                                        <div><button className={`submit-btn start-stream-btn${props._connecting ? ' button-inactive' : ''}`} onClick={props._connecting ? null : props._startStream}>{props._connecting ? 'Finalising Connection...' : 'Record Analytics'}<br />{props._connecting ? 'Please Wait' : 'and Start Bots'}</button></div>
                                    </div>
                                </div>
                            }
                            {props._streamStarted &&
                                <>
                                    <div className="stream-manager-summary">
                                        <div className="stream-manager-time-row">
                                            <CirclePerc perc={liveTimePerc < 100 ? liveTimePerc : 100}>
                                                {IconJsxer.GetIcon(IconJsxer.ICONS.clock, IconJsxer.ICON_STYLES.streamControlOptions)}
                                            </CirclePerc>
                                            <div className="timer-data stream-manager-timer-data">
                                                <p className="light-text">Stream Active Time</p>
                                                <h2 className="stream-manager-live-time">{`${liveTimeObj.rawHours}h ${liveTimeObj.minutesStr}m ${liveTimeObj.secondsStr}s`}</h2>
                                                <p className="light-text small-text">Target: {`${targetTimeHms.h}h ${targetTimeHms.m_str}m ${targetTimeHms.s_str}s`}</p>
                                            </div>
                                        </div>
                                        <div className="stream-manager-stats">
                                            <div className="stream-manager-stat-row">
                                                <div className="stream-manager-stat-circle stream-manager-stat-circle--messages">
                                                    {IconJsxer.GetIcon(IconJsxer.ICONS.comments, IconJsxer.ICON_STYLES.analyticsKey)}
                                                </div>
                                                <div className="stream-manager-stat-copy">
                                                    <h3>{props._analyticsData.messages ? <>{props._analyticsData.messages.toLocaleString()}</> : <>0</>}</h3>
                                                    <p>Total Messages</p>
                                                </div>
                                            </div>
                                            <div className="stream-manager-stat-row">
                                                <div className="stream-manager-stat-circle an-light-yellow-bg">
                                                    {IconJsxer.GetIcon(IconJsxer.ICONS.hashtag, IconJsxer.ICON_STYLES.analyticsKey)}
                                                </div>
                                                <div className="stream-manager-stat-copy">
                                                    <h3>{props._analyticsData.interactions ? <>{props._analyticsData.interactions.toLocaleString()}</> : <>0</>}</h3>
                                                    <p>Interactions</p>
                                                </div>
                                            </div>
                                            <div className="stream-manager-stat-row">
                                                <div className="stream-manager-stat-circle an-light-purple-bg">
                                                    {IconJsxer.GetIcon(IconJsxer.ICONS.multiUsers, IconJsxer.ICON_STYLES.analyticsKey)}
                                                </div>
                                                <div className="stream-manager-stat-copy">
                                                    <h3>{totalUniqueUsers.toLocaleString()}</h3>
                                                    <p>Unique Users</p>
                                                </div>
                                            </div>
                                            <div className="stream-manager-stat-row">
                                                <div className="stream-manager-stat-circle stream-manager-stat-circle--engagement">
                                                    {IconJsxer.GetIcon(IconJsxer.ICONS.bullhorn, IconJsxer.ICON_STYLES.analyticsKey)}
                                                </div>
                                                <div className="stream-manager-stat-copy">
                                                    <h3>{CalculateEngagementTime(props._analyticsData)}</h3>
                                                    <p>Feature Engagement</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="stream-manager-analytics">
                                        <div className="underlined-panel-heading stream-manager-analytics-heading">
                                            <h4>Channel Analytics</h4>
                                            <DropDownList
                                                id="stream_manager_channel_filter"
                                                selectFunc={updateChannelFilterHandler}
                                                currentIndex={channelFilter ? channelFilter.index : 0}
                                                icon="oneUser"
                                                items={channelDropdownItems}
                                                className="filter"
                                            />
                                            <div className="grow"></div>
                                            <div className="fl-row compact stream-manager-analytics-key light-text">
                                                <div className="circle-color-block an-light-blue-bg">
                                                    {IconJsxer.GetIcon(IconJsxer.ICONS.comments, IconJsxer.ICON_STYLES.analyticsKey)}
                                                </div>
                                                <div>Messages</div>
                                            </div>
                                            <div className="fl-row compact stream-manager-analytics-key light-text">
                                                <div className="circle-color-block an-light-yellow-bg">
                                                    {IconJsxer.GetIcon(IconJsxer.ICONS.hashtag, IconJsxer.ICON_STYLES.analyticsKey)}
                                                </div>
                                                <div>Interactions</div>
                                            </div>
                                            <div className="fl-row compact stream-manager-analytics-key light-text">
                                                <div className="circle-color-block an-light-purple-bg">
                                                    {IconJsxer.GetIcon(IconJsxer.ICONS.multiUsers, IconJsxer.ICON_STYLES.analyticsKey)}
                                                </div>
                                                <div>Users</div>
                                            </div>
                                            <div className="fl-row compact light-text">
                                                {IconJsxer.GetIcon(IconJsxer.ICONS.clock, IconJsxer.ICON_STYLES.analyticsClock)}
                                                <FormDropDown
                                                    className="stream-manager-analytics-drop"
                                                    id="stream_manager_chart_time"
                                                    showLabel={false}
                                                    showErrors={false}
                                                    items={[
                                                        { id: '10_sec', label: '10 sec', value: MS_TIMES.SECS_10 },
                                                        { id: '30_sec', label: '30 sec', value: MS_TIMES.SECS_30 },
                                                        { id: '1_min', label: '1 min', value: MS_TIMES.MINS_1 },
                                                        { id: '5_min', label: '5 min', value: MS_TIMES.MINS_5 },
                                                        { id: '15_min', label: '15 min', value: MS_TIMES.MINS_15 },
                                                    ]}
                                                    selectFunc={(e, id, item) => { setSelectedMessagePerTime(item); setMessagePerTime(item.value); }}
                                                    currentIndex={selectedMessagePerTime !== null ? selectedMessagePerTime.index : 0}
                                                />
                                            </div>
                                        </div>

                                        <div className="stream-manager-analytics-chart">
                                            <ResponsiveContainer width="100%" height={210}>
                                                <AreaChart
                                                    data={chartData}
                                                    isAnimationActive={true}
                                                    margin={{ top: 5, right: 12, left: -24, bottom: 5 }}
                                                >
                                                    <CartesianGrid stroke="var(--mid-grey-2)" strokeOpacity={0.28} />
                                                    <XAxis dataKey="time" />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Legend />
                                                    <Area type="monotone" isAnimationActive={true} fillOpacity={0.9} strokeWidth={2.15} name="Messages" dataKey="messages" fill="#79C8F2" stroke="#4C9ECE" />
                                                    <Area type="monotone" isAnimationActive={true} fillOpacity={0.8} strokeWidth={1.85} name="Interactions" dataKey="interactions" fill="#FFD581" stroke="#D9AF57" />
                                                    <Area type="monotone" isAnimationActive={true} fillOpacity={0.7} strokeWidth={1.75} name="New Chat Users" dataKey="uniqueMessageUsers" fill="#8C6CFF" stroke="#6D4CF0" activeDot={{ r: 4, stroke: '#6D4CF0', strokeWidth: 2, fill: '#8C6CFF' }} />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                            {!hasChartAnalytics &&
                                                <div className="light-text stream-manager-analytics-empty">Analytics chart will appear once the stream session starts.</div>
                                            }
                                        </div>
                                    </div>
                                </>
                            }
                        </div>

                {/* Ad Bug hidden — no longer in use */}
                    </div>
                </div>
                <div className="column">
                    <div className="stream-manager-section-box">
                        <div className="underlined-panel-heading">
                            <h4>Stream Transcript</h4>
                            <div className="grow"></div>
                        </div>
                        <div className="tr-scroll-area">
                            {(!props._analyticsData.transcript || props._analyticsData.transcript.length === 0) &&
                                <div>No Activity Yet Reported</div>
                            }
                            {props._analyticsData.transcript && props._analyticsData.transcript.map(
                            (val, i) => {
                                return (
                                    <StreamTranscriptItem
                                        key = {'transcript'+i}
                                        itemData={{
                                            type:val.transcriptType,
                                            startTime: val.startTime,
                                            endTime: val.endTime,
                                            shortDescription:val.shortDescription,
                                            longDescription:val.longDescription
                                        }}
                                        _showTooltip={props._showTooltip}
                                        _hideTooltip={props._hideTooltip}
                                    />
                                )
                            }
                            )}
                        </div>
                    </div>
                </div>
            </div>

        </div>
    )
}

export default TabStreamManager;
